/**
 * TrendChart: a Carbon Charts bar or line chart of one series, always with the same data as a real table behind a
 * "Chart / Table" switch (the chart is not accessible on its own). The chart library loads on first use, in the
 * `white` theme; its stylesheet loads with it (`chartLib.ts`, bundled, no CDN; apps don't depend on the package). A single
 * series has no legend.
 *
 * Props: `type` ('bar' | 'line'), `points` (`{ label, value }`), `labelHeader` and `valueHeader` (table headers),
 * `summary` (the chart's accessible name), `formatValue` (use `useFormat`).
 */
import { ContentSwitcher, Switch, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@carbon/react';
import type { BarChartOptions } from '@carbon/charts';
import { useTranslation } from '@pos/i18n';
import { lazy, Suspense, useMemo, useState } from 'react';
import { SkeletonText } from '@carbon/react';
import './overview.scss';

const SimpleBarChart = lazy(() => import('./chartLib').then((m) => ({ default: m.SimpleBarChart })));
const LineChart = lazy(() => import('./chartLib').then((m) => ({ default: m.LineChart })));

export interface TrendPoint {
  label: string;
  value: number;
}

export interface TrendChartProps {
  type: 'bar' | 'line';
  points: readonly TrendPoint[];
  labelHeader: string;
  valueHeader: string;
  summary: string;
  formatValue(value: number): string;
}

export function TrendChart({ type, points, labelHeader, valueHeader, summary, formatValue }: TrendChartProps) {
  const { t } = useTranslation();
  const [view, setView] = useState<'chart' | 'table'>('chart');
  const data = useMemo(
    () => points.map((p) => ({ group: valueHeader, key: p.label, value: p.value })),
    [points, valueHeader],
  );
  const options = useMemo(
    () =>
      ({
        theme: 'white',
        // No Carbon token for a chart's height: a fixed 16rem plot, one line comment as DESIGN_SYSTEM.md §13 notes.
        height: '16rem',
        resizable: true,
        legend: { enabled: false },
        toolbar: { enabled: false },
        axes: {
          left: { mapsTo: 'value', ticks: { formatter: (v: number) => formatValue(v) } },
          bottom: { mapsTo: 'key', scaleType: 'labels' },
        },
        tooltip: { valueFormatter: (v: number) => formatValue(v) },
        ...(type === 'line' ? { points: { enabled: true } } : {}),
      }) as unknown as BarChartOptions,
    [formatValue, type],
  );
  const Chart = type === 'bar' ? SimpleBarChart : LineChart;

  return (
    <div className="pos-trend-chart">
      <ContentSwitcher
        selectedIndex={view === 'chart' ? 0 : 1}
        onChange={({ name }) => setView(name === 'table' ? 'table' : 'chart')}
        aria-label={t('admin.overview.chart.view')}
      >
        <Switch name="chart" text={t('admin.overview.chart.chart')} />
        <Switch name="table" text={t('admin.overview.chart.table')} />
      </ContentSwitcher>
      {view === 'chart' ? (
        <div role="img" aria-label={summary} className="pos-trend-chart__plot">
          <Suspense fallback={<SkeletonText paragraph lineCount={4} />}>
            <Chart data={data} options={options} />
          </Suspense>
        </div>
      ) : (
        <Table aria-label={summary}>
          <TableHead>
            <TableRow>
              <TableHeader>{labelHeader}</TableHeader>
              <TableHeader className="pos-trend-chart__number">{valueHeader}</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {points.map((p) => (
              <TableRow key={p.label}>
                <TableCell>{p.label}</TableCell>
                <TableCell className="pos-trend-chart__number">{formatValue(p.value)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
